// frontend/src/utils/use-project.data.ts
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import i18n from "@/i18n";
import api from "@/utils/axios";
import { ProjectFieldMetadata } from "@/types/ProjectFieldMetadata";

export interface Project {
  [key: string]: string | number | null;
}

export function useProjectData() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [projectFieldMetadata, setProjectFieldMetadata] = useState<ProjectFieldMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjectData = useCallback(async (opts?: { background?: boolean }) => {
    if (!id) {
      setLoading(false);
      setError(i18n.t("project.errors.missing_project_id"));
      return;
    }

    const background = Boolean(opts?.background);
    if (!background) setLoading(true);

    try {
      const [projectRes, fieldsRes] = await Promise.all([
        api.get(`/api/projects/${id}`),
        api.get(`/api/project-fields`),
      ]);

      setProject(projectRes.data);
      setProjectFieldMetadata(fieldsRes.data);
      setError(null);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || i18n.t("common.error");
      setError(msg);
    } finally {
      if (!background) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // expose stable refetch
  const refetch = useCallback(
    (opts?: { background?: boolean }) => fetchProjectData(opts),
    [fetchProjectData]
  );

  return { project, projectFieldMetadata, loading, error, setProject, refetch };
}
